import { createSlice } from '@reduxjs/toolkit';
import * as robotCleaningProgressActionTypes from './robotCleaningProgressActions.types';
import type { Optional } from 'lib/types/Optional';
import type { CleaningTaskState } from 'app/modules/machine-inventory/interfaces/CleaningTask.types';

export interface RobotCleaningProgressEntry extends CleaningTaskState {
  status: 'loading' | 'open' | 'error';
  deviceTaskId: Optional<string>;
  progress: number;
  step: Optional<number>;
  stepCount: Optional<number>;
  estimatedEndTime: Optional<string>;
  error: Optional<unknown>;
}

// The robot's current cleaning task, keyed by robotId: several cards can show it at once.
export interface RobotCleaningProgressState {
  byRobotId: Record<string, RobotCleaningProgressEntry>;
}

export const initialState: RobotCleaningProgressState = {
  byRobotId: {},
};

const emptyTaskFields = {
  deviceTaskId: null,
  progress: 0,
  name: null,
  subName: null,
  taskStatus: null,
  mapId: null,
  step: null,
  stepCount: null,
  estimatedEndTime: null,
};

// Within a route of one task the progress only grows, so a lower value is a reset before the step moves on.
const keepProgressWithinStep = (
  previous: Optional<RobotCleaningProgressEntry>,
  deviceTaskId: Optional<string>,
  step: Optional<number>,
  progress: number
): number =>
  previous && step != null && step === previous.step && deviceTaskId === previous.deviceTaskId
    ? Math.max(previous.progress, progress)
    : progress;

const robotCleaningProgressSlice = createSlice({
  name: 'robot-cleaning-progress',
  initialState,
  reducers: {
    openRobotCleaningProgressRequest: (
      state,
      action: robotCleaningProgressActionTypes.OpenRobotCleaningProgressRequestAction
    ) => {
      state.byRobotId[action.payload.robotId] = { ...emptyTaskFields, status: 'loading', error: null };
      return state;
    },
    openRobotCleaningProgressSuccess: (
      state,
      action: robotCleaningProgressActionTypes.OpenRobotCleaningProgressSuccessAction
    ) => {
      const { robotId, ...task } = action.payload;
      const progress = keepProgressWithinStep(state.byRobotId[robotId], task.deviceTaskId, task.step, task.progress);
      state.byRobotId[robotId] = { ...task, progress, status: 'open', error: null };
      return state;
    },
    robotCleaningProgressRefreshed: (
      state,
      action: robotCleaningProgressActionTypes.RobotCleaningProgressRefreshedAction
    ) => {
      const { robotId, ...task } = action.payload;

      if (state.byRobotId[robotId]) {
        const previous = state.byRobotId[robotId];
        const progress = keepProgressWithinStep(previous, task.deviceTaskId, task.step, task.progress);
        // A poll without the route's end time must not wipe the one the subscription gave for the same step.
        const estimatedEndTime =
          task.estimatedEndTime ??
          (task.step != null && task.step === previous.step ? previous.estimatedEndTime : null);
        state.byRobotId[robotId] = { ...task, progress, estimatedEndTime, status: 'open', error: null };
      }

      return state;
    },
    openRobotCleaningProgressError: (
      state,
      action: robotCleaningProgressActionTypes.OpenRobotCleaningProgressErrorAction
    ) => {
      const { robotId, error } = action.payload;
      state.byRobotId[robotId] = { ...emptyTaskFields, status: 'error', error };
      return state;
    },
    closeRobotCleaningProgress: (state, action: robotCleaningProgressActionTypes.CloseRobotCleaningProgressAction) => {
      delete state.byRobotId[action.payload.robotId];
      return state;
    },
    robotCleaningProgressUpdated: (
      state,
      action: robotCleaningProgressActionTypes.RobotCleaningProgressUpdatedAction
    ) => {
      const entry = state.byRobotId[action.payload.robotId];

      if (entry) {
        entry.progress = keepProgressWithinStep(
          entry,
          entry.deviceTaskId,
          action.payload.step ?? entry.step,
          action.payload.progress
        );

        // The end time belongs to the route being cleaned, so the previous route's must not carry over to the next.
        // An update of the same route may leave it out, and then the known one stays.
        const hasStepChanged = action.payload.step != null && action.payload.step !== entry.step;

        if (hasStepChanged) {
          entry.estimatedEndTime = action.payload.estimatedEndTime ?? null;
        } else if (action.payload.estimatedEndTime) {
          entry.estimatedEndTime = action.payload.estimatedEndTime;
        }

        if (action.payload.step != null) {
          entry.step = action.payload.step;
        }

        if (action.payload.stepCount != null) {
          entry.stepCount = action.payload.stepCount;
        }
      }

      return state;
    },
    // A subscription event may carry only some of the task's fields; keep the stored value for the rest.
    robotCleaningTaskUpdated: (state, action: robotCleaningProgressActionTypes.RobotCleaningTaskUpdatedAction) => {
      const { robotId, ...fields } = action.payload;
      const entry = state.byRobotId[robotId];

      if (entry) {
        entry.name = fields.name ?? entry.name;
        entry.subName = fields.subName ?? entry.subName;
        entry.taskStatus = fields.taskStatus ?? entry.taskStatus;
        entry.mapId = fields.mapId ?? entry.mapId;
      }

      return state;
    },
  },
});

export const RobotCleaningProgressActions = {
  ...robotCleaningProgressSlice.actions,
};
export const robotCleaningProgressReducer = robotCleaningProgressSlice.reducer;
